import axiosInstance from 'utils/axiosUtil';
import { setChatbotState }        from '../../store/actions/appActions';
import {
  createChatBotMessage,
  createClientMessage
} from 'react-chatbot-kit-extension';
import routeUpdateState from './updateStateRouter';
import { CLAIMS_RANGE_MSG } from 'utils/claimsRange';

/*
import CLAIMS_CFG from '../../../config/claims-date.js';   // adjust path if needed

const claimEnd = new Date(CLAIMS_CFG.latestClaimsDate ?? CLAIMS_CFG);
const claimStart   = new Date(claimEnd);
claimStart.setFullYear(claimEnd.getFullYear() - 1);

const CLAIMS_RANGE_MSG =
  'Claims data available from ' +
  `${claimStart.toISOString().slice(0,10)} through ` +
  `${claimEnd.toISOString().slice(0,10)}.`;

*/

export default class AudioConversation {

  constructor() {
    // this should be private only, use getInstance() to access
    this.model = 'gpt-4o-realtime-preview-2025-06-03';
    // this.model = 'gpt-realtime-2025-08-28';
    this.voice = 'alloy';
    this.temperature = 0.6;
    this.speed = 1.0;
    this.conversationSpecificPrompt = 'You are a helpful, witty, and friendly AI.  Speak quickly. Speak in English.';
    //this.conversationSpecificPrompt = 'You are a helpful, witty, and friendly AI.  
    // Your voice and personality should be warm and engaging, with a lively and playful tone. Speak quickly. Speak in English.';    

  // run-time fields
    this.peerConnection      = null;
    this.dataChannel         = null;
    this.localStream         = null;
    this.token               = null;
    this.endingConversation  = false;
    this.sessionId           = 0;
    this.initPromise         = null;
    this.pendingUsage        = null;
    this.currentUrl          = typeof window !== 'undefined' ? window.location.pathname : '';

    this.autoShutdownMs = 90_000;    // 90 seconds
    this._shutdownTimer = null;

    this.state = { active: false, speaking: false, listening: false };

  }
  
  static getInstance() {
    if (!AudioConversation.instance) {
      AudioConversation.instance = new AudioConversation();
    }
    return AudioConversation.instance;
  }

  // helpers
  getState() {
    const s = this.state || { active: false, speaking: false, listening: false };
    return { ...s };
  }
  
  _clearShutdown() {
    if (this._shutdownTimer) {
      clearTimeout(this._shutdownTimer);
      this._shutdownTimer = null;
    }
  }

  // 90s of INACTIVITY: call this whenever there is activity to re-arm the timer
  _pokeShutdown() {
    // Don’t keep arming if the session isn’t up
    if (!this.peerConnection) return;
    this._clearShutdown();
    this._shutdownTimer = setTimeout(() => {
      if (!this.endingConversation) {
        console.log('[audio] auto-shutdown (90s inactivity)');
        this.endConversation();   // will say “Goodbye”
      }
    }, this.autoShutdownMs);
  }

  // Detect which tool (if any) was used in this turn
  _detectToolFromResponse(response) {
    const out = Array.isArray(response?.output) ? response.output : [];

    // 1) Function tools
    const fn = out.find((o) => o?.type === 'function_call');
    if (fn) {
     return { kind: 'function', name: fn.name || null, call_id: fn.call_id || null };
    }

    // 2) file_search (newer "tool_use" shape)
    const tu = out.find(
      (o) => o?.type === 'tool_use' && ((o.name || o.tool_name) === 'file_search')
    );
    if (tu) {
      return { kind: 'file_search', id: tu.id || null };
    }

    // 3) file_search (older shapes)
    const fs = out.find(
     (o) => typeof o?.type === 'string' && o.type.startsWith('file_search')
    );
    if (fs) {
      return { kind: 'file_search', id: fs.id || null };
    }

    // No tool used (pure text answer)
    return { kind: null };
  }

  async stopNow() {
    // cancel any shutdown/goodbye timers if you added them
    try { this._clearShutdown?.(); } catch {}
    try { this._clearGoodbyeTimers?.(); } catch {}

    // stop mic stream
    if (this.localStream) {
      try { this.localStream.getTracks().forEach((t) => t.stop()); } catch {}
      this.localStream = null;
    }

    // close RTC/data channel
    try { this.dataChannel?.close(); } catch {}
    this.dataChannel = null;

    try { this.peerConnection?.close(); } catch {}
    this.peerConnection = null;

    // reset state and notify UI
    this._setState?.({ active: false, speaking: false, listening: false });

    // clear flags
    this.endingConversation = false;
    this.sessionId++; // make stale any in-flight ops
  }



  _emitState() {
    window.dispatchEvent(new CustomEvent('audio-state', { detail: this.getState() }));
  }
  
  _setState(patch) {
    this.state = { ...this.state, ...patch };
    this._emitState();
  }
  
  setCurrentUrl(url) {
    this.currentUrl = url || (typeof window !== 'undefined' ? window.location.pathname : '');
  }

  // Call this to update the model with the new URL during an active session
  updateUrl(url) {
    this.setCurrentUrl(url);
    if (this.isConversationStarted()) {
      this.sendMessage({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user', // this could be 'system' too?
        content: [ { type: 'input_text', text: `User changed path to: ${this.currentUrl}` } ],
      },
      });

    }

  }

  async init(id) {
    this.endingConversation = false;
    if (await this.requestMicrophonePermission()) {
      await this.getToken();
      await this.createConnection(id);
    } else {
      throw new Error('Please allow microphone access to use audio conversations.');
    }
  }

  async requestMicrophonePermission() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (error) {
      console.error('Microphone permission denied:', error);
      return false;
    }
  }

  async getToken() {
    this.token = (await axiosInstance.post('/AI', {
      type: 'real-time-token-request',
      model: this.model,
      voice: this.voice,
      temperature: this.temperature,
      current_url: this.currentUrl, 
    })).data.token;
  }

  async getPrompt() {
    return (await axiosInstance.post('/AI', {
      type: 'get-prompt',
    })).data.prompt;
  }

  async getTools() {
    return (await axiosInstance.post('/AI', {
      type: 'get-tools',
    })).data.tools;
  }


  async createConnection(id) {
    const aborted = () => id !== this.sessionId;

    try {
      // Peer connection
      this.peerConnection = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      if (aborted()) return;

      // Remote audio
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.playsInline = true;
      this.peerConnection.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
      };

      // Local mic
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (aborted()) {
        ms.getTracks().forEach((t) => t.stop());
        return;
      }

      this.localStream = ms;
      ms.getAudioTracks().forEach((track) => {
        this.peerConnection.addTrack(track, ms);
        // Mic unplugged / permission revoked
        track.addEventListener('ended', () => {
          this._setState({ active: false, speaking: false, listening: false });
          this.checkEndConversation();
        });
      });

      // Immediately reflect “mic available, listening”
      this._setState({ active: true, speaking: false, listening: true });

      // Connection state
      this.peerConnection.onconnectionstatechange = () => {
        const s = this.peerConnection.connectionState;
        if (s === 'connected') {
          this._setState({ active: true, listening: true, speaking: false });
        } else if (['disconnected', 'failed', 'closed'].includes(s)) {
          this._setState({ active: false, speaking: false, listening: false });
        }
      };

      // ICE state (some browsers fire this more reliably)
      this.peerConnection.oniceconnectionstatechange = () => {
        const s = this.peerConnection.iceConnectionState;
        if (s === 'connected' || s === 'completed') {
          this._setState({ active: true, listening: true });
        } else if (['disconnected', 'failed', 'closed'].includes(s)) {
          this._setState({ active: false, speaking: false, listening: false });
        }
      };

      // Data channel
      this.dataChannel = this.peerConnection.createDataChannel('oai-events');
      this.dataChannel.addEventListener('open', () => this.onDataChannelOpen());
      this.dataChannel.addEventListener('message', (e) => this.onDataChannelMessage(e));

      // SDP Offer/Answer
      const offer = await this.peerConnection.createOffer({
        offerToReceiveAudio: true,
        voiceActivityDetection: true,
      });
      await this.peerConnection.setLocalDescription(offer);

      if (aborted()) return;

      const baseUrl = 'https://api.openai.com/v1/realtime';
      const sdpResponse = await fetch(`${baseUrl}?model=${this.model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (aborted()) return;
      if (!sdpResponse.ok) {
        throw new Error(`SDP exchange failed: ${sdpResponse.status} ${sdpResponse.statusText}`);
      }

      const answerSdp = await sdpResponse.text();
      const answer = { type: 'answer', sdp: answerSdp };
      await this.peerConnection.setRemoteDescription(answer);
    } catch (err) {
      console.error('createConnection error:', err);
      // Cleanup
      try { this.dataChannel?.close(); } catch {}
      this.dataChannel = null;

      try { this.peerConnection?.close(); } catch {}
      this.peerConnection = null;

      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
        this.localStream = null;
      }
      this._setState({ active: false, speaking: false, listening: false });
      throw err;
    }
  }

  async OLDcreateConnection(id) {
     const aborted = () => id !== this.sessionId;

    // Create a peer connection
    this.peerConnection = new RTCPeerConnection();
    if (aborted()) return;

    // Set up to play remote audio from the model
    const audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    this.peerConnection.ontrack = (e) => audioEl.srcObject = e.streams[0];

    // Add local audio track for microphone input in the browser
    const ms = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    if (aborted()) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
      return;
    }
    //this.peerConnection.addTrack(ms.getTracks()[0]);

    this.localStream = ms;
    const track = ms.getTracks()[0];
    this.peerConnection.addTrack(track);

    // mic unplugged / permission revoked, etc.
    track.addEventListener('ended', () => {
      this._setState({ active: false, speaking: false, listening: false });
      this.checkEndConversation();
    });

    // connection state (network issues, ICE failure, etc.)
    this.peerConnection.onconnectionstatechange = () => {
      const s = this.peerConnection.connectionState;
      if (s === 'connected') {
        this._setState({ active: true, listening: true, speaking: false });
      } else if (['disconnected', 'failed', 'closed'].includes(s)) {
        this._setState({ active: false, speaking: false, listening: false });
      }
    };




    // Set up data channel for sending and receiving events
    this.dataChannel = this.peerConnection.createDataChannel('oai-events');
    this.dataChannel.addEventListener('open', () => { this.onDataChannelOpen(); });
    this.dataChannel.addEventListener('message', (e) => { this.onDataChannelMessage(e); });

    // Start the session using the Session Description Protocol (SDP)
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    
    if (aborted()) return;
    const baseUrl = 'https://api.openai.com/v1/realtime';
    const sdpResponse = await fetch(`${baseUrl}?model=${this.model}`, {
      method: 'POST',
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${this.token}`,
        'Content-Type': 'application/sdp',
      },
    });
    if (aborted()) return;
    
    const answer = {
      type: 'answer',
      sdp: await sdpResponse.text(),
    };
    await this.peerConnection.setRemoteDescription(answer);
  }

  isConversationStarted() {
    return !!this.peerConnection;
  }

  startConversation(router, user, clientId, dispatchFn, initialState) {
    this.endingConversation = false;
    this.router = router;
    this.user = user;
    this.clientId = clientId;
    this.dispatch= dispatchFn; 
    this.history = (initialState.messages || []).slice();
    this.setCurrentUrl(typeof window !== 'undefined' ? window.location.pathname : '');
    const id = ++this.sessionId;
    if (!this.peerConnection) {
      this.init(id);
      // this.createConnection();
    }
  }
    

  async endConversation() {
    // 0. prevent double-calls
    if (this.endingConversation) return;

    this.endingConversation = true;

    // Stop any further auto-shutdown triggers
    this._clearShutdown();

    /* 2. ask the assistant to say good-bye, in English */
    await this.sendMessage({
      type: 'response.create',
      response: {
        modalities   : ['text', 'audio'],
        instructions : 'Say in English: Goodbye.',          // note the colon
      },
    });
  }  
 
 
  async checkEndConversation() {
    if (!this.endingConversation) return;
    if (this.endingConversation) {
      
      console.log('Ending session...');

      /* stop mic stream */
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
        this.localStream = null;
      }

    if (this.dataChannel)     { try { this.dataChannel.close(); }     catch {} this.dataChannel = null; }
    if (this.peerConnection)  { try { this.peerConnection.close(); }  catch {} this.peerConnection = null; }

    this._setState({ active: false, speaking: false, listening: false });

      this._clearShutdown();     // be extra sure it’s cleared
      this.endingConversation = false;
      this.sessionId++;
    }
  }

  async onDataChannelOpen() {
    console.log('Data channel opened');
  }
  
  async onDataChannelMessage(event) {
    try {
      const data = JSON.parse(event.data);
      if (!data.timestamp) {
        data.timestamp = new Date().toLocaleTimeString();
      }

      // --- 1. good-bye detector --------------------------------------
      if (
        data.type === 'response.audio_transcript.done' ||
        data.type === 'conversation.item.input_audio_transcription.completed'
      ) {
        const spoken =
          (data.transcript ||
          data.response?.output?.find((o) => o.type === 'text')?.text ||
          ''
          ).trim().toLowerCase();

        if (['goodbye', 'bye', 'good-bye', 'good bye'].includes(spoken)) {
          await this.endConversation();   // graceful hang-up
          return;                         // <- stop further processing
        }
        // FALL THROUGH so the normal handlers can still run
      }
    
     if (data.type === 'assistant.tool_call_created'
          && data.tool_call.name === 'search_private_docs') {

        // ➊ read the arguments the model sent
        const { query, top_k = 3 } =
              JSON.parse(data.tool_call.arguments);

        // ➋ run your own vector search
        const passages = await axiosInstance.post(
          '/AI',                      // or call your backend directly
          { type:'execute-function',
            function_name:'search_private_docs',
            args:{ query, top_k },
            audio_mode:true }
        ).then((r) => r.data.passages ?? [])
         .catch(() => []);

        // ➌ return the result to the model
        await this.sendMessage({
          type: 'assistant.tool_result',
          tool_call_id: data.tool_call.id,
          content: JSON.stringify({ passages })
        });

        //return;   // nothing else to do for this WS event

      /* ---------------------------------------------------------*
      * 1.  assistant finished the turn  →  response.done
      * ---------------------------------------------------------*/      
      
      } else if (data.type === 'response.done') {
        const spoken =
          data.response.output?.find((o) => o.type === 'text')?.text ?? '';

        if (spoken) this._appendTranscript('assistant', spoken);

        // ★ save usage & model for later logging
        this.pendingUsage = {
          usage : data.response.usage || {},
          model : data.response.model || this.model
        };

        /* ∙∙∙ Did the model call a tool? */
        const fn = data.response.output?.find(
          (o) => o.type === 'function_call'
        );
        if (fn) {
          await this.handleFunctionsMessage(data);
        }

        const tool = this._detectToolFromResponse(data.response);
        console.log('TOOL USED: ');
        console.dir(tool.name);


        this._pokeShutdown();           // assistant finished a turn
      } else if (data.type === 'response.audio_transcript.done') {
        const transcript = data.transcript;
        this._appendTranscript('assistant', transcript);

        // pull usage that was stored a moment ago 
        const { usage:u = {} } = this.pendingUsage || {};
//        const u = data.response?.usage || {};
        await axiosInstance.post('/AI', {
          type     : 'log-usage',
          user_id  : this.user.id,
          client_id: this.clientId,
          current_url: this.currentUrl,
          context  : {
            mode       : 'Audio',
            model      : data.response?.model ?? null,
            input_toks : u.input_tokens          ?? 0,
            output_toks: u.output_tokens         ?? 0,
            total_toks : u.total_tokens          ?? 0,
            cached_toks: u.input_tokens_details?.cached_tokens ?? 0,
            answer     : transcript,              // ← always filled
          }
        });
        this._pokeShutdown();           // assistant transcript is ready
      } else if (data.type === 'conversation.item.input_audio_transcription.completed') {
        // user finished talking; still active, now waiting for assistant
        this._setState({ listening: false });
        console.log('user transcript done:', data.transcript);
        this._appendTranscript('user', data.transcript);
        const spoken = (data.transcript || '').trim().toLowerCase();
        if (spoken === 'close chat') { await this.endConversation(); return; }
        this._pokeShutdown();           // user finished talking

      // console.log(data);
      } else if (data.type === 'session.created') {
        this.onSessionCreated();



      } else if (data.type === 'output_audio_buffer.stopped') {
        this._setState({ speaking: false, listening: this.state.active });
        window.dispatchEvent(new Event('assistant-silent'));
        this.checkEndConversation(); // check if we are ending the conversation
        // mode change to listening

        this._pokeShutdown();           // assistant done speaking

      } else if (data.type === 'output_audio_buffer.started') {
         this._setState({ speaking: true, listening: false });
        // mode change to speaking
          window.dispatchEvent(new Event('assistant-speaking'));
          this._pokeShutdown();           // assistant done speaking
      } else if (data.type === 'response.audio_transcript.delta') {
        // ignore these

      } else if (data.type === 'response.done' && data.response?.output?.some((o) => (o.type == 'function_call'))) {
        console.log('Function call detected:', data.response.output);
        this.handleFunctionsMessage(data);

      } else if (data.type === 'error') {
        console.log('ERROR:', data);

      } else {
        // console.log('unhandled:', data);
      }

    } catch (error) {
      console.error('Error processing data channel message:', error);
    }
  }

async handleFunctionsMessage(data) {
    const fnOutput = data.response.output.find((o) => o.type === 'function_call');
    if (!fnOutput) return;

    const args = typeof fnOutput.arguments === 'string'
                 ? JSON.parse(fnOutput.arguments)
                 : fnOutput.arguments;

    /* Execute the function */
    const result = await this.onFunctionCall(fnOutput.name, args);

    /* ★  Send function_call_output  +  response.create
       ------------------------------------------------ */
    await this.sendMessage({
      type: 'conversation.item.create',
      item: {
        type   : 'function_call_output',
        call_id: fnOutput.call_id,
        output : JSON.stringify(result),
      },
    });
    await this.sendMessage({ type: 'response.create' });
  }

  async onFunctionCall(name, args) {
    /* take_me_there is handled locally */
    if (name === 'take_me_there') {
      if (args?.path) {
        this.router?.navigate(args.path);
        // Update session URL context after navigation
        const abs = typeof window !== 'undefined'
          ? window.location.origin + args.path
          : args.path;
        this.updateUrl(abs);
        
      /* tell the server so it can log the action */
         axiosInstance.post('/AI', {
           type          : 'execute-function',
           function_name : 'take_me_there',
           args          : { path: args.path, audio_mode:true },
           user_id       : this.user.id,
           client_id     : this.clientId,
           current_url   : this.currentUrl
         }).catch((e) => console.warn('[audio] take_me_there log failed:', e));


        return { success: true, message: `Navigated to ${args.path}` };
      }
      return { error: 'Path was not provided.' };
    }

    /* all other functions -> back-end */
    const resp = await axiosInstance.post('/AI', {
      type          : 'execute-function',
      function_name : name,
      args,
      user_id       : this.user.id,
      client_id     : this.clientId,
      audio_mode    : true,
      current_url   : this.currentUrl,
    });

    if (resp.status !== 200 || !resp.data)
      return { error: `Failed to execute: ${name}` };

    routeUpdateState(resp.data.update_state, this.dispatch, this.clientId, this.router);
    return resp.data;
  }
 
  _appendTranscript(role, text) {
    if (!text || !text.trim() || !this.dispatch) return;

    const nextMessage =
      role === 'user'
        ? createClientMessage(text)
        : createChatBotMessage(text, { loading:false });

    this.history = [...this.history, nextMessage];

    /* 1. write the growing array to Redux so the panel that is
          currently open sees the new line immediately            */
    this.dispatch(setChatbotState({ messages: this.history}));

    /* 2. also broadcast how many messages are now stored so the
          next ActionProvider can fetch them when it mounts       */
    window.sessionStorage.setItem(
      'audio-history-length',
      String(this.history.length),
    );
  }

  async onSessionCreated() {
    try {
      let prompt = await this.getPrompt();

      const isAdmin = !!(this.user?.is_admin ?? this.user?.isAdmin);
      const roleGuard = isAdmin
        ? 'Important policy: The current user is an administrator. Administrators must not create or update Activity Notes. ' +
          'Never propose, attempt, or call any function to create or update an Activity Note. ' +
          'Offer read-only help (e.g., viewing or navigating to notes) instead.'
        : '';

    // Inject URL into the instructions so the assistant knows where we are
      const pathLine = `Current page path: ${this.currentUrl}`;

      prompt = [pathLine, roleGuard, this.conversationSpecificPrompt, CLAIMS_RANGE_MSG, prompt]
        .filter(Boolean)
        .join('\n\n');

      console.log('PROMPT LENGTH: ', prompt.length);
      if (prompt.length > 65000) {
        prompt = prompt.substring(0, 65000);
      }

      let tools = await this.getTools();
//      tools = tools.filter((t) => t && ['function', 'file_search'].includes(t.type));
      tools.forEach((t) => delete t.strict);   // realtime doesn’t support “strict”


      /***************************************************************
       * Realtime does NOT yet support the built-in `"file_search"`
       * tool.  Expose retrieval as a custom function instead.
       **************************************************************/
      const retrievalFn = {
        type: 'function',
        name: 'search_private_docs',
        description: 'Search Doctivity’s private knowledge base and return the most relevant passages.',
        parameters: {
          type: 'object',
          properties: {
            query: { type: 'string', description: 'User question' },
            top_k: { type: 'integer', default: 5 }
          },
          required: ['query']
        }
      };

      // keep only custom functions we already had, then add retrievalFn
      tools = tools.filter((t) => t && t.type === 'function');
      tools.push(retrievalFn);



      // Remove forbidden tools for admins
      if (isAdmin) {
        const FORBIDDEN = new Set(['create_activity_note', 'update_activity_note']);
        tools = tools.filter((t) => !FORBIDDEN.has(t.name));
      }
    

      console.log('++++++++++++++++++++++++++++++');
      console.log(tools);
      console.log('++++++++++++++++++++++++++++++');


      console.log('Sending tools:', JSON.stringify(tools, null, 2));

      const policy = 'When the user asks about company facts or leadership ' +
               'that may be in Doctivity’s private knowledge base, ' +
               'ALWAYS call the function search_private_docs first.';

      await this.sendMessage({
        type: 'session.update',
        session: {
          instructions: [policy, pathLine, prompt, policy].join('\n\n'),
          tools,
          tool_choice: 'auto',
        },
      });

      let today = new Date();
      let curHr = today.getHours();
      let time = 'evening';
      if (curHr < 12) {
        time = 'morning';
      } else if (curHr < 18) {
        time = 'afternoon';
      }

      const firstQuestion =
        `Say: 'Good ${time} ` +
        `${this.user.first_name_pronunciation?.trim() || this.user.first_name}. ` +
        'My name is Key, Doctivity\'s AI assistant. How may I help you?\'';
      await this.sendMessage({
        type: 'response.create',
        response: {
          modalities: ['text', 'audio'],
          instructions: firstQuestion,
        },
      });

      // Arm the inactivity timer once the session is ready
      this._pokeShutdown();

    } catch (error) {
      console.error('Error during session creation:', error);
    }
  }

  async sendMessage(message) {
    message.event_id = message.event_id || crypto.randomUUID();
    console.log('sending message:', message);
    this.dataChannel?.send(JSON.stringify(message));
  }  

}